import { environment } from '../../../../environments/environment';
import { Paginated } from '../../../core/models/paginated.model';

/** A sales rep who can drive a vehicle. */
export interface SalesRepRef {
  id: string;
  name: string;
  nameAr: string;
  /** Local mobile number, e.g. `10 1122 3344`. */
  mobile: string;
}

export interface LoadingWarehouse {
  id: string;
  name: string;
  nameAr: string;
  /** SAP Business One warehouse code, e.g. `01`. */
  sapCode: string;
  /** Route the warehouse serves, e.g. "Dokki". */
  route: string;
  routeAr: string;
}

/**
 * A vehicle from SAP, with the sales rep and default zone set here (B51).
 * Fields the API does not send yet are empty (`''` or `null`) and show as "—".
 */
export interface Vehicle {
  id: string;
  /** Licence plate. Not in the API yet: the vehicle's SAP label is shown instead. */
  plate: string;
  plateAr: string;
  /** The vehicle's SAP warehouse code, e.g. `06`. */
  vanWarehouse: string;
  /** Delivers orders and also sells from its own stock. The same for every vehicle (`VEHICLE_CASH_VAN`). */
  cashVan: boolean;
  /** Where its orders and stock load from. The same for every vehicle (`VEHICLE_LOADS_FROM`). */
  loadsFrom: LoadingWarehouse;
  salesRep: SalesRepRef | null;
  /** Route code, e.g. `R-07`; '' while the API does not send it. */
  route: string;
  /** Id of the vehicle's default zone; '' when none is set. */
  zoneId: string;
  /** Name of the default zone, e.g. "Dokki"; '' when none is set. */
  routeArea: string;
  routeAreaAr: string;
}

/** Every vehicle delivers orders and sells from its own stock. */
export const VEHICLE_CASH_VAN = true;

/** Every vehicle loads from the main warehouse set in the environment (`mainWarehouse`). */
export const VEHICLE_LOADS_FROM: LoadingWarehouse = {
  id: environment.mainWarehouse.code,
  name: environment.mainWarehouse.name,
  nameAr: environment.mainWarehouse.name,
  sapCode: environment.mainWarehouse.code,
  route: environment.mainWarehouse.route,
  routeAr: environment.mainWarehouse.routeAr,
};

/** One page of `GET /vehicles`. */
export interface VehicleList extends Paginated<Vehicle> {
  /** ISO date-time of the last vehicle sync from SAP; '' while the API does not send it. */
  lastSync: string;
}

/** `GET /vehicles` query; `search` matches the plate (label), SAP warehouse or sales rep, '' for all. */
export interface VehiclesQuery {
  page: number;
  pageSize: number;
  search: string;
}

/** A delivery zone to use as the vehicle's default route. */
export interface ZoneOption {
  id: string;
  name: string;
  nameAr: string;
}

export interface SalesRepOption extends SalesRepRef {
  /** Id and label of the vehicle the rep is on, if any. */
  vehicleId: string | null;
  plate: string | null;
}

/** What B52's sales rep and zone dropdowns offer (`GET /vehicles/options`). */
export interface VehicleOptions {
  salesReps: SalesRepOption[];
  /** Labelled in the page's language. */
  zones: ZoneOption[];
}

export interface VehicleBody {
  salesRepId: string;
  zoneId: string;
}
